// @flow

/**
 * memoize caches the results of a function call so future calls with the same
 * parameter can be returned quickly. Different from lodash, memoizeOne only
 * caches one result at a time. Future calls of the wrapped function with
 * different parameters will result in the cache being flushed and a new
 * response calculated.
 *
 * This util can be useful in React, particularly with v16 lifecycle methods
 * because deriving state from props can sometimes be an expensive operation and
 * at times it's important to get the same exact ref-type object back for the
 * sake of sCUP comparison. For more info, see "What about memoization?" at
 * https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html
 */

type MemoryItem = {|
  context: mixed,
  args: Array<mixed>,
  result: mixed,
|};

type Comparator = (a: Array<mixed>, b: Array<mixed>) => boolean;
type AnyFunction = (...Array<any>) => mixed;

function shallowEquals(a: any, b: any) {
  if (typeof a === 'object' && typeof b === 'object') {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);

    return aKeys.length === bKeys.length && aKeys.every((aKey) => a[aKey] === b[aKey]);
  }

  return a === b;
}

// argsAreEqualWithRefChecks is the most basic comparitor -- it just iterates
// over each function arg in order and does a "===" comparison.
export const argsAreEqualWithRefChecks: Comparator = (a: Array<mixed>, b: Array<mixed>) =>
  a.length === b.length && a.every((val: mixed, index: number) => val === b[index]);

// argsAreEqualWithShallowEqualsChecks is like argsAreEqualWithRefChecks, but if
// it encounters an object, it iterates over that object's keys instead of just
// doing a "===" comparison.
export const argsAreEqualWithShallowEqualsChecks: Comparator = (a: Array<mixed>, b: Array<mixed>) =>
  a.length === b.length && a.every((val: mixed, index: number) => shallowEquals(val, b[index]));

// Default comparitor is argsAreEqualWithRefChecks. This is the same behavior as
// lodash's memoize.
const DEFAULT_COMPARATOR = argsAreEqualWithRefChecks;

// createMemoizeFunc is a memoize function factory. It returns a memoize
// function with the given cache size and comparitor. This memoize function can
// then be used (and re-used) to memoize other functions.
export const createMemoizeFunc = (
  itemLimit?: number,
  comparator: Comparator = DEFAULT_COMPARATOR,
) => <F: AnyFunction>(func: F): F => {
  const memory: Array<MemoryItem> = [];

  function memoize(...args: Array<mixed>) {
    const context = this;

    const memoryMatch = memory.find(
      (item) => item.context === context && comparator(item.args, args),
    );

    if (memoryMatch) {
      return memoryMatch.result;
    }

    const newMemoryItem = {
      context,
      args,
      result: func.apply(this, args),
    };

    memory.push(newMemoryItem);

    if (!!itemLimit && memory.length > itemLimit) {
      memory.shift();
    }

    return newMemoryItem.result;
  }

  return (memoize: any);
};

// Create and export the most common memoize use cases.
export const memoizeOne = createMemoizeFunc(1);
export const memoize = createMemoizeFunc();
export default memoize;
